import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  ManageScene,
  ManageSceneKind,
  ManageScenePhoto,
  ManageSceneVideo,
  PhotoSortMode,
} from '../entities/gallery-manage.models';
import type {
  AddVideoResult,
  DeleteSceneResult,
  PhotoDeleteResult,
  ScenesResult,
  UpdateVideoResult,
} from './gallery-manage.data';

// gallery-service errorCode of a video file still queued for deletion (gallery-scenes.service.ts).
const VIDEO_FILE_STILL_DELETING_CODE = 'VIDEO_FILE_STILL_DELETING';

// gallery-service errorCode of a scene whose photos sit in an approved list (gallery-scenes.service.ts).
const SCENE_LOCKED_CODE = 'SCENE_LOCKED';

// gallery-service GallerySceneDto (gallery-scenes.service.ts) — the manage
// scene read carries photos[] and videos[] embedded, no separate photo call.
interface WireScenePhoto {
  id: string;
  sceneId: string;
  lumirFileId: string;
  thumbnailUrl: string | null;
  orientation: 'horizontal' | 'vertical';
  sortOrder: number;
  isClientPrivate?: boolean;
  originalName?: string;
  processingStatus?: string;
  // Renditions: the server drops a nullish column instead of sending null.
  smallUrl?: string;
  mediumUrl?: string;
  originalUrl?: string;
  width?: number;
  height?: number;
  thumbHash?: string;
  dominantColor?: string;
}

interface WireSceneVideo {
  id: string;
  provider: string;
  url: string;
  embedUrl: string;
  fileUrl?: string | null;
  sortOrder: number;
  posterPhotoId?: string | null;
  posterThumbnailUrl?: string | null;
}

interface WireScene {
  id: string;
  galleryId: string;
  name: string;
  kind: string;
  description: string | null;
  coverThumbnailUrl: string | null;
  isHidden: boolean;
  photoSortMode: string;
  sortOrder: number;
  photoCount: number;
  videoCount: number;
  lockedPhotoCount?: number;
  photos: WireScenePhoto[];
  videos: WireSceneVideo[];
}

@Injectable({ providedIn: 'root' })
export class GalleryManageApiService {
  private readonly http = inject(HttpClient);

  // 403/404 = чужая (или несуществующая) галерея — отдельный исход, экран показывает
  // «Галерея недоступна» вместо предложения повторить.
  async getScenes(galleryId: string): Promise<ScenesResult> {
    try {
      const wire = await firstValueFrom(
        this.http.get<WireScene[]>(`/api/gallery-scenes/gallery/${encodeURIComponent(galleryId)}`),
      );
      return { ok: true, scenes: wire.map(toScene) };
    } catch (error) {
      const denied = error instanceof HttpErrorResponse && (error.status === 403 || error.status === 404);
      return { ok: false, reason: denied ? 'no_access' : 'error' };
    }
  }

  // Frozen-order splice: targetOrderedIds is the client's DISPLAYED target order,
  // toIndex the slot among the remaining (payload removed) photos. One endpoint
  // covers within-scene and cross-scene drops alike.
  async movePhotosToPosition(
    galleryId: string,
    photoIds: readonly string[],
    targetSceneId: string,
    targetOrderedIds: readonly string[],
    toIndex: number,
  ): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.post(`/api/gallery-photos/${encodeURIComponent(galleryId)}/move-to-position`, {
          photoIds,
          targetSceneId,
          targetOrderedIds,
          toIndex,
        }),
      );
      return true;
    } catch {
      return false;
    }
  }

  async movePhotosToGallery(galleryId: string, photoIds: readonly string[], targetGalleryId: string): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.post(`/api/gallery-photos/${encodeURIComponent(galleryId)}/move-to-gallery`, {
          photoIds,
          targetGalleryId,
        }),
      );
      return true;
    } catch {
      return false;
    }
  }

  // The delete endpoint returns a workflow envelope with a 200 even for the
  // locked / cover_warning refusals — read the body, don't just catch a throw.
  async deletePhotos(galleryId: string, photoIds: readonly string[], confirmCover?: boolean): Promise<PhotoDeleteResult> {
    try {
      const body = await firstValueFrom(
        this.http.post<PhotoDeleteResult>(`/api/gallery-photos/${encodeURIComponent(galleryId)}/delete`, {
          photoIds,
          ...(confirmCover ? { confirmCover: true } : {}),
        }),
      );
      return body;
    } catch {
      return { ok: false, reason: 'error' };
    }
  }

  // Перезапуск обработки одного фото после отказа конвейера; тело ответа
  // ({restarted}) UI не нужно — важен сам факт принятого перезапуска.
  async reprocessPhoto(photoId: string): Promise<boolean> {
    try {
      await firstValueFrom(this.http.post(`/api/gallery-photos/${encodeURIComponent(photoId)}/reprocess`, {}));
      return true;
    } catch {
      return false;
    }
  }

  async reorderScenes(galleryId: string, orderedIds: readonly string[]): Promise<boolean> {
    try {
      await firstValueFrom(this.http.put('/api/gallery-scenes/reorder', { galleryId, orderedIds }));
      return true;
    } catch {
      return false;
    }
  }

  async setVideoPoster(videoId: string, photoId: string | null): Promise<boolean> {
    try {
      await firstValueFrom(this.http.put(`/api/gallery-scenes/videos/${encodeURIComponent(videoId)}/poster`, { photoId }));
      return true;
    } catch {
      return false;
    }
  }

  // name omitted → the server assigns the positional auto-name. Returns the new
  // scene id so the caller can re-read to get its full (server-shaped) row.
  async addScene(galleryId: string, name?: string): Promise<string | null> {
    try {
      const res = await firstValueFrom(
        this.http.post<{ id: string }>('/api/gallery-scenes', name ? { galleryId, name } : { galleryId }),
      );
      return res.id;
    } catch {
      return null;
    }
  }

  // POST /video: server converts an empty photo scene in place or appends a new
  // video scene, atomically with the first video. 400/422 = the URL isn't a
  // recognised provider embed; anything else is an unexpected failure.
  async addVideo(galleryId: string, sceneId: string, url: string, name?: string): Promise<AddVideoResult> {
    try {
      const res = await firstValueFrom(
        this.http.post<{ sceneId: string; created: boolean }>('/api/gallery-scenes/video', {
          galleryId,
          sceneId,
          url,
          ...(name ? { name } : {}),
        }),
      );
      return { ok: true, sceneId: res.sceneId };
    } catch (error) {
      if (error instanceof HttpErrorResponse && (error.status === 400 || error.status === 422)) {
        return { ok: false, reason: 'invalid_url' };
      }

      return { ok: false, reason: 'unknown' };
    }
  }

  // POST /video-file: same anchor behaviour as /video, but the source is an already-
  // uploaded file's public URL kept verbatim as provider='upload'.
  async addVideoFile(
    galleryId: string,
    sceneId: string,
    fileUrl: string,
    fileId?: string,
    name?: string,
  ): Promise<AddVideoResult> {
    try {
      const res = await firstValueFrom(
        this.http.post<{ sceneId: string; created: boolean }>('/api/gallery-scenes/video-file', {
          galleryId,
          sceneId,
          fileUrl,
          ...(fileId ? { fileId } : {}),
          ...(name ? { name } : {}),
        }),
      );
      return { ok: true, sceneId: res.sceneId };
    } catch (error) {
      if (error instanceof HttpErrorResponse && (error.status === 400 || error.status === 422)) {
        // The refusal is told apart by its machine code, never by the text in `error`.
        const code = (error.error as { errorCode?: unknown } | null)?.errorCode;
        return { ok: false, reason: code === VIDEO_FILE_STILL_DELETING_CODE ? 'file_still_deleting' : 'invalid_url' };
      }

      return { ok: false, reason: 'unknown' };
    }
  }

  // PUT :sceneId/video: one video per scene, so the edit window's link+name save is a
  // single call — the server re-derives the provider and renames the scene in the same
  // transaction, and answers with the id of the video row it kept or created.
  async updateVideo(sceneId: string, url: string, name: string): Promise<UpdateVideoResult> {
    try {
      const res = await firstValueFrom(
        this.http.put<{ id: string }>(`/api/gallery-scenes/${encodeURIComponent(sceneId)}/video`, {
          url,
          ...(name ? { name } : {}),
        }),
      );
      return { ok: true, videoId: res.id };
    } catch (error) {
      if (error instanceof HttpErrorResponse && (error.status === 400 || error.status === 422)) {
        return { ok: false, reason: 'invalid_url' };
      }

      return { ok: false, reason: 'unknown' };
    }
  }

  async renameScene(sceneId: string, name: string): Promise<boolean> {
    try {
      await firstValueFrom(this.http.put(`/api/gallery-scenes/${encodeURIComponent(sceneId)}/name`, { name }));
      return true;
    } catch {
      return false;
    }
  }

  async setSceneHidden(sceneId: string, isHidden: boolean): Promise<boolean> {
    try {
      await firstValueFrom(this.http.put(`/api/gallery-scenes/${encodeURIComponent(sceneId)}/hidden`, { isHidden }));
      return true;
    } catch {
      return false;
    }
  }

  // Одним запросом на всю галерею — сервер проставляет режим каждой её сцене.
  async setGallerySortMode(galleryId: string, sortMode: PhotoSortMode): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.put(`/api/gallery-scenes/galleries/${encodeURIComponent(galleryId)}/sort-mode`, { sortMode }),
      );
      return true;
    } catch {
      return false;
    }
  }

  // 409 SCENE_LOCKED with details.lockedCount when a scene photo sits in an approved list. The code
  // alone marks the refusal as locked: a body without the count still yields `locked`.
  async deleteScene(sceneId: string): Promise<DeleteSceneResult> {
    try {
      await firstValueFrom(this.http.delete(`/api/gallery-scenes/${encodeURIComponent(sceneId)}`));
      return { ok: true };
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 409) {
        const body = error.error as { errorCode?: unknown; details?: { lockedCount?: number } } | null;
        if (body?.errorCode === SCENE_LOCKED_CODE) return { ok: false, locked: body.details?.lockedCount ?? 0 };
      }

      return { ok: false };
    }
  }

  async setSceneDescription(sceneId: string, description: string): Promise<boolean> {
    try {
      await firstValueFrom(this.http.put(`/api/gallery-scenes/${encodeURIComponent(sceneId)}/description`, { description }));
      return true;
    } catch {
      return false;
    }
  }

  async clearSceneDescription(sceneId: string): Promise<boolean> {
    try {
      await firstValueFrom(this.http.delete(`/api/gallery-scenes/${encodeURIComponent(sceneId)}/description`));
      return true;
    } catch {
      return false;
    }
  }
}

function toKind(kind: string): ManageSceneKind {
  return kind === 'video' ? 'video' : 'photo';
}

function toPhoto(p: WireScenePhoto): ManageScenePhoto {
  return {
    id: p.id,
    thumbnailUrl: p.thumbnailUrl,
    isClientPrivate: p.isClientPrivate ?? false,
    sortOrder: p.sortOrder,
    originalName: p.originalName ?? null,
    // An older gateway sends nothing — the photo is then treated as ready, never
    // as a stuck spinner.
    processingStatus: p.processingStatus === 'processing' || p.processingStatus === 'failed' ? p.processingStatus : 'ready',
    smallUrl: p.smallUrl ?? null,
    mediumUrl: p.mediumUrl ?? null,
    originalUrl: p.originalUrl ?? null,
    width: p.width ?? null,
    height: p.height ?? null,
    thumbHash: p.thumbHash ?? null,
    dominantColor: p.dominantColor ?? null,
  };
}

function toVideo(v: WireSceneVideo): ManageSceneVideo {
  return {
    id: v.id,
    provider: v.provider,
    url: v.url,
    embedUrl: v.embedUrl,
    fileUrl: v.fileUrl ?? null,
    posterPhotoId: v.posterPhotoId ?? null,
    posterThumbnailUrl: v.posterThumbnailUrl ?? null,
  };
}

const SORT_MODES: readonly PhotoSortMode[] = [
  'manual',
  'uploadedNew',
  'uploadedOld',
  'shotAtNew',
  'shotAtOld',
  'nameAsc',
  'nameDesc',
  'random',
];

// Legacy name/shotAt (server may still emit them) fold to the current equivalent.
function toSortMode(value: string): PhotoSortMode {
  if ((SORT_MODES as readonly string[]).includes(value)) return value as PhotoSortMode;
  if (value === 'name') return 'nameAsc';
  if (value === 'shotAt') return 'shotAtOld';
  return 'manual';
}

function toScene(w: WireScene): ManageScene {
  return {
    id: w.id,
    name: w.name,
    kind: toKind(w.kind),
    description: w.description,
    isHidden: w.isHidden,
    photoSortMode: toSortMode(w.photoSortMode),
    sortOrder: w.sortOrder,
    photoCount: w.photoCount,
    videoCount: w.videoCount,
    lockedPhotoCount: w.lockedPhotoCount ?? 0,
    photos: w.photos.map(toPhoto),
    videos: w.videos.map(toVideo),
  };
}
